export type WeaponModKind = 'scope' | 'extended_mag' | 'silencer';

export type WeaponMod = {
  kind: WeaponModKind;
};

/** Slot A: scope or silencer (mutually exclusive). Slot B: extended mag only. */
export type WeaponModSlot = 'A' | 'B';

export type WeaponModSet = {
  slotA?: WeaponMod;
  slotB?: WeaponMod;
};

export const MOD_CHEST_ROLL_CHANCE = 0.18;
/** When a chest rolls a mod, 5% chance to roll a second mod as well. */
export const MOD_CHEST_DOUBLE_ROLL_CHANCE = 0.05;

const MOD_LABELS: Record<WeaponModKind, string> = {
  scope: 'Scope',
  extended_mag: 'Extended Mag',
  silencer: 'Silencer',
};

const MOD_ICONS: Record<WeaponModKind, string> = {
  scope: 'S',
  extended_mag: 'M',
  silencer: 'X',
};

/** ADS spread multiplier when scope is attached (35% tighter). */
export const SCOPE_ADS_SPREAD_MULT = 0.65;

/** Magazine size multiplier for extended mag (+50%). */
export const EXTENDED_MAG_MULT = 1.5;

/** Damage multiplier for silencer (-15% damage). */
export const SILENCER_DAMAGE_MULT = 0.85;

export function getModSlot(kind: WeaponModKind): WeaponModSlot {
  return kind === 'extended_mag' ? 'B' : 'A';
}

export function getModDisplayName(kind: WeaponModKind): string {
  return MOD_LABELS[kind];
}

export function getModIconLetter(kind: WeaponModKind): string {
  return MOD_ICONS[kind];
}

/** HUD chip text — up to two letters separated by space, e.g. "S M". */
export function getModHudLetters(set: WeaponModSet): string {
  const letters: string[] = [];
  if (set.slotA) letters.push(getModIconLetter(set.slotA.kind));
  if (set.slotB) letters.push(getModIconLetter(set.slotB.kind));
  return letters.join(' ');
}

export function getModsList(set: WeaponModSet): WeaponMod[] {
  const mods: WeaponMod[] = [];
  if (set.slotA) mods.push(set.slotA);
  if (set.slotB) mods.push(set.slotB);
  return mods;
}

export function getModAttachFeedLine(kind: WeaponModKind): string {
  return `Attached ${MOD_LABELS[kind]}`;
}

function rollRandomMod(): WeaponMod {
  const kinds: WeaponModKind[] = ['scope', 'extended_mag', 'silencer'];
  const kind = kinds[Math.floor(Math.random() * kinds.length)]!;
  return { kind };
}

/** 18% chance for at least one mod; 5% of those rolls include a second mod. */
export function rollModsFromChest(): WeaponMod[] {
  if (Math.random() >= MOD_CHEST_ROLL_CHANCE) return [];
  const mods = [rollRandomMod()];
  if (Math.random() < MOD_CHEST_DOUBLE_ROLL_CHANCE) {
    mods.push(rollRandomMod());
  }
  return mods;
}
